/**
 * Global admission gate for bundle chunk fetches — a semaphore, not a
 * scheduler.
 *
 * StorageProxy caps concurrent bundle streams at 8, process-wide per Cloud Run
 * instance, shared across every reader that instance serves. One browser tab
 * can already exceed that alone today: a demand page delivery spawns its own
 * 3-worker pool per page and does not wait for one page to settle before
 * requesting the next, so a five-page series can have up to 15 chunk streams
 * in flight at once. Adding the prefetch path's one background stream on top
 * makes the aggregate worth bounding centrally.
 *
 * `BUNDLE_SLOTS_TOTAL` is well under the proxy's 8 specifically because that 8
 * is shared with every other radiologist on the instance — a tab that takes
 * all of it just moves the 429s onto colleagues. Of the total,
 * `BUNDLE_SLOTS_DEMAND_RESERVED` are never available to the background
 * (prefetch) lane, so demand — what the reader is actively waiting on — never
 * queues behind speculative background streaming: a freed slot is always
 * offered to a waiting demand request before a waiting background one.
 *
 * See docs/planning/2026-08-25-bundle-aware-prefetch.md, "Demand preemption
 * and the proxy's admission cap".
 */
export const BUNDLE_SLOTS_TOTAL = 4;
export const BUNDLE_SLOTS_DEMAND_RESERVED = 2;
let heldTotal = 0;
let heldBackground = 0;
const queue = [];
function backgroundSlotsFree() {
    return heldBackground < BUNDLE_SLOTS_TOTAL - BUNDLE_SLOTS_DEMAND_RESERVED;
}
function canAdmit(lane) {
    if (heldTotal >= BUNDLE_SLOTS_TOTAL)
        return false;
    return lane === 'demand' || backgroundSlotsFree();
}
function detachAbortListener(waiter) {
    if (waiter.signal && waiter.onAbort) {
        waiter.signal.removeEventListener('abort', waiter.onAbort);
    }
}
function grant(lane) {
    heldTotal++;
    if (lane === 'background')
        heldBackground++;
    let released = false;
    return {
        release() {
            if (released)
                return;
            released = true;
            heldTotal--;
            if (lane === 'background')
                heldBackground--;
            drainQueue();
        },
    };
}
/**
 * Offer freed slots to queued waiters. Demand goes first — it may use any
 * free slot up to {@link BUNDLE_SLOTS_TOTAL} — so a slot a background release
 * just freed is offered to a waiting demand request before a waiting
 * background one. Only once demand has taken everything it can does
 * background get a look at what (if anything) remains within its own
 * reserved-aware ceiling.
 */
function drainQueue() {
    for (const lane of ['demand', 'background']) {
        let i = 0;
        while (i < queue.length) {
            const waiter = queue[i];
            if (waiter.lane !== lane) {
                i++;
                continue;
            }
            if (!canAdmit(lane))
                break;
            queue.splice(i, 1);
            detachAbortListener(waiter);
            waiter.resolve(grant(lane));
            // Do not advance i — the splice shifted the next candidate into it.
        }
    }
}
/**
 * Acquire a slot in `lane`, resolving once one is available.
 *
 * When `signal` is supplied and aborts while this call is still queued (not
 * yet granted a slot), the wait is cancelled and the promise rejects with the
 * signal's abort reason — the waiter is removed from the queue rather than
 * left to consume a slot nobody will use. Abort has no effect once a slot has
 * already been granted; the returned handle's `release()` is the only way to
 * give it back.
 */
export function acquireBundleSlot(lane, signal) {
    if (signal?.aborted) {
        return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    }
    if (canAdmit(lane)) {
        return Promise.resolve(grant(lane));
    }
    return new Promise((resolve, reject) => {
        const waiter = { lane, resolve, reject, signal };
        if (signal) {
            const onAbort = () => {
                const index = queue.indexOf(waiter);
                if (index === -1)
                    return; // already admitted
                queue.splice(index, 1);
                reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
            };
            waiter.onAbort = onAbort;
            signal.addEventListener('abort', onAbort, { once: true });
        }
        queue.push(waiter);
    });
}
/** Slots currently held, for tests and diagnostics. */
export function bundleSlotsHeld() {
    return heldTotal;
}
/** Acquire calls currently queued (no slot granted yet), for tests. */
export function bundleSlotsQueued() {
    return queue.length;
}
/**
 * Forget all held slots and queued waiters. Call between tests; never call
 * with real work outstanding, since it does not reject or resolve anything it
 * discards.
 */
export function resetBundleQueue() {
    heldTotal = 0;
    heldBackground = 0;
    queue.length = 0;
}
